 
import {analyticsService} from '@/ts/services/analytics-service';
import type {UserInfoData} from '@/ts/types/posthog';
import type {Language} from '@/ts/types/component/localization.type';

/** Contract for analytics tracking operations including user identification, events, and feature flags */
export interface Analytics {
  identifyUser(userData: UserInfoData | null): void;
  updateUserProperties(properties: Record<string, any>): void;
  setAlias(alias: string, original: string): void;
  setLanguage(language: Language): void;
  trackEvent(event: string, properties?: Record<string, any>): void;
  trackPageView(pageName: string): void;
  trackButtonClick(buttonName: string): void;
  trackFormSubmit(formName: string, success: boolean): void;
  isFeatureEnabled(flagName: string, defaultValue?: boolean): boolean;
  reset(): void;
}

/** Provides analytics tracking methods for user identification, events, page views, and feature flags */
export function useAnalytics(): Analytics {
  return {
    // Core methods
    identifyUser: (userData: UserInfoData | null): void => { analyticsService.identifyUser(userData); },
    updateUserProperties: (properties: Record<string, any>): void => { analyticsService.updateUserProperties(properties); },
    setAlias: (alias: string, original: string): void => { analyticsService.setAlias(alias, original); },
    setLanguage: (language: Language): void => { analyticsService.setLanguage(language); },
    trackEvent: (event: string, properties?: Record<string, any>): void => 
      { analyticsService.trackEvent(event, properties); },
    
    // Helper methods for common events
    trackPageView: (pageName: string): void => { analyticsService.trackEvent('page_view', {pageName}); },
    trackButtonClick: (buttonName: string): void => { analyticsService.trackEvent('button_click', {buttonName}); },
    trackFormSubmit: (formName: string, success: boolean): void => 
      { analyticsService.trackEvent('form_submit', {formName, success}); },
    
    // Feature flags
    isFeatureEnabled: (flagName: string, defaultValue = false): boolean => 
      analyticsService.isFeatureEnabled(flagName, defaultValue),
    reset: (): void => { analyticsService.reset(); },
  };
} 
